<template>
  <PanelCard :title="I18n.t(keys.title, { count: items.length })">
    <template #action>
      <SfButtonDs
        variant="secondary"
        size="sm"
        icon-before="plus"
        :data-testid="`group-panel-add-${list.kind}s`"
        @click="picking = !picking"
      >
        {{ I18n.t(keys.add) }}
      </SfButtonDs>
    </template>

    <p class="tw-m-0 tw-text-xs tw-text-sf-slate-light-2">
      {{ I18n.t(keys.hint) }}
    </p>

    <GroupPicker
      v-if="picking"
      :testid="`group-panel-${list.kind}-picker`"
      :placeholder="I18n.t(keys.search)"
      :search="list.search"
      :request-key="list.requestKey"
      :exclude-ids="itemIds"
      @select="onSelect"
    />

    <SfInpageList v-if="items.length">
      <SfInpageListItem
        v-for="item in items"
        :key="item.id"
        :title="item.name"
        :item-id="item.id"
        :icon-size="iconSize"
      >
        <template #icon>
          <slot name="icon" :item="item" />
        </template>
        <template v-if="$slots.subtitles" #subtitles>
          <slot name="subtitles" :item="item" />
        </template>
        <template #right>
          <SfButtonDs
            variant="secondary"
            size="sm"
            :aria-label="`${I18n.t('group.panel.remove')} ${item.name}`"
            :data-testid="`group-panel-${list.kind}-remove`"
            @click="emit('remove', item.id)"
          >
            {{ I18n.t('group.panel.remove') }}
          </SfButtonDs>
        </template>
      </SfInpageListItem>
    </SfInpageList>

    <p
      v-else
      class="
        tw-m-0
        tw-rounded
        tw-bg-sf-slate-subtle-1
        tw-py-10
        tw-text-center
        tw-text-sm
        tw-text-sf-slate-light-2
      "
      :data-testid="`group-panel-${list.kind}s-empty`"
    >
      {{ I18n.t(keys.empty) }}
    </p>
  </PanelCard>
</template>

<script setup lang="ts" generic="T extends GroupPickerItem">
import type { GroupList } from '@app/application/user-management/group-lists'
import type { GroupPickerItem } from '@app/application/user-management/types'

import { computed, ref } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import GroupPicker from '@app/application/user-management/components/groups/group-picker.vue'
import PanelCard from '@app/application/user-management/components/panel-card.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'

defineOptions({ name: 'GroupItemList' })

const props =
  defineProps<{
    list: GroupList<T>
    items: T[]
    iconSize?: number
  }>()

const emit =
  defineEmits<{
    add: [item: T]
    remove: [id: number]
  }>()

defineSlots<{
  icon(props: { item: T }): unknown
  subtitles?(props: { item: T }): unknown
}>()

const I18n = useI18n()

const picking = ref(false)

const keys = computed(() => props.list.keys)

const itemIds = computed(() => props.items.map((item) => item.id))

function onSelect(item: T): void {
  picking.value = false

  emit('add', item)
}
</script>
